'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import Portal from './Portal';

const Dropdown = ({
  // Required parameters with defaults
  placeholder = "Academic Year : 2025 / 2026",
  text_font_size = "text-sm",
  text_font_family = "Inter",
  text_font_weight = "font-normal",
  text_line_height = "leading-md",
  text_text_align = "left",
  text_color = "text-text-secondary",
  fill_background_color = "bg-dropdown-background",
  border_border = "border border-dropdown-border",
  border_border_radius = "rounded-md",

  // Optional parameters
  dropdownAlign = 'left', // 'left' or 'right'
  zIndex = 'z-50', // Custom z-index for dropdown menu
  layout_align_self,

  layout_gap,
  layout_width,
  padding,
  position,

  // Standard React props
  options = [],
  value,
  onChange,
  className,
  disabled = false,
  searchable = false,
  /**
   * Let the CALLER own the search.
   *
   * By default the search box filters the options it was handed. Some
   * pickers cannot work that way: a student list is capped server-side
   * (the first 50 matches), so typing a name that is not in those 50
   * must re-query, not filter nothing. When this is provided the local
   * filter is switched off — the parent is fetching matches and handing
   * back the ones that count.
   *
   * This exists so those screens stop needing a SECOND search box above
   * the field, which is what they used to do.
   */
  onSearchChange,
  /** Replaces the generic "Search..." — say what can be searched on. */
  searchPlaceholder = 'Search...',
  /** A small note under the search box, inside the menu. */
  menuNote = '',
  // Multi-select mode: `value` is an ARRAY of selected values; the menu
  // stays open on click, each row shows a checkbox, and `onChange` fires
  // the toggled option (the parent maintains the array). The trigger
  // shows the selected labels (or placeholder when none).
  multiSelect = false,
  // Optional: render a rich row for each option. Receives the option
  // object, returns a React node. The plain `label` string is still
  // used for the trigger text and search, so keep it set.
  renderOption,
  // Pulled out of ...props so the search input and option rows can
  // derive stable child ids; re-applied to the trigger button below.
  'data-testid': dataTestId,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(value || placeholder)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)

  const optValueOf = (opt) => (opt && typeof opt === 'object' ? opt.value : opt);

  useEffect(() => {
    if (multiSelect) {
      const arr = Array.isArray(value) ? value : [];
      const labels = (options || [])
        .filter((opt) => arr.includes(optValueOf(opt)))
        .map((opt) => opt?.label || opt);
      setSelectedValue(labels.length ? labels.join(', ') : placeholder);
    } else if (value) {
      const selectedOption = options?.find((opt) =>
        (opt && typeof opt === 'object' ? opt.value === value : opt === value)
      );
      setSelectedValue(selectedOption ? (selectedOption.label || selectedOption) : value);
    } else {
      setSelectedValue(placeholder);
    }
  }, [value, placeholder, options, multiSelect])

  // Safe validation for optional parameters
  const hasValidAlignSelf = layout_align_self && typeof layout_align_self === 'string' && layout_align_self?.trim() !== ''
  const hasValidGap = layout_gap && typeof layout_gap === 'string' && layout_gap?.trim() !== ''
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width?.trim() !== ''
  const hasValidPadding = padding && typeof padding === 'string' && padding?.trim() !== ''
  const hasValidPosition = position && typeof position === 'string' && position?.trim() !== ''

  const optionalClasses = [
    hasValidAlignSelf ? `self-${layout_align_self}` : '',
    hasValidWidth && layout_width === 'full' ? 'w-full' : '',
    hasValidPosition ? position : '',
  ]?.filter(Boolean)?.join(' ')

  // Dimensions arrive as raw CSS lengths ("200px", "10px"), so they are
  // applied as INLINE STYLE, not as Tailwind classes.
  //
  // These used to build `w-[${layout_width}]` / `gap-[${layout_gap}]` /
  // `p-[${padding}]` at runtime. Tailwind's JIT only emits classes it finds
  // as literal strings while scanning source, so a class assembled from a
  // prop is never generated — the element silently got NO width, the
  // trigger collapsed to its text, and the menu (sized from the trigger's
  // bounding box) wrapped every option onto two lines.
  // `full` keeps using the real `w-full` class, which does exist literally.
  const sizingStyles = {
    ...(hasValidWidth && layout_width !== 'full' && { width: layout_width }),
    ...(hasValidGap && { gap: layout_gap }),
    ...(hasValidPadding && { padding }),
  }

  // Build custom styles for non-Tailwind properties
  const customStyles = {
    ...(text_font_family && !text_font_family?.startsWith('font-') && { fontFamily: text_font_family }),
    ...(text_text_align && text_text_align !== 'left' && { textAlign: text_text_align }),
  }

  // Build Tailwind classes for styling
  const styleClasses = [
    text_font_size,
    text_font_family?.startsWith('font-') ? text_font_family : '',
    text_font_weight,
    text_line_height,
    text_color,
    fill_background_color,
    border_border,
    border_border_radius,
    'px-3 py-2',
    'cursor-pointer',
    'focus:outline-none focus:ring-2 focus:ring-primary-background focus:border-transparent',
    'transition-all duration-200',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
  ]?.filter(Boolean)?.join(' ')

  // Calculate menu position when dropdown opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  const dropdownMenuRef = useRef(null); // Ref for the portal content

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the dropdown wrapper (button) OR inside the dropdown menu (portal)
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!dropdownMenuRef.current || !dropdownMenuRef.current.contains(event.target))
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option) => {
    // Multi-select: keep the menu open and let the parent toggle the value.
    if (multiSelect) {
      if (typeof onChange === 'function') onChange(option)
      return
    }
    setSelectedValue(option?.label || option)
    setIsOpen(false)

    if (typeof onChange === 'function') {
      onChange(option)
    }
  }

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // A caller-owned search is NOT cleared on close, and deliberately so.
      // The caller's option list is the search RESULT, so clearing it would
      // re-fetch the unfiltered list — and the option the user just picked
      // may not be in it, which makes the field fall back to its
      // placeholder and read as though nothing was chosen. Keeping the term
      // also means reopening shows the same box and the same list, rather
      // than an empty box over filtered results.
      if (!onSearchChange) setSearchTerm('');
    }
    // Focusing the search box lives in `focusSearchInput` (a callback ref)
    // rather than here: the menu is rendered through <Portal>, which paints
    // nothing on its first pass, so at the time this effect runs the input
    // does not exist yet and a ref check here always fails.
  }, [isOpen, onSearchChange]);

  // Callback ref: runs the moment the search input is actually in the DOM
  // (one render after the menu opens, because of Portal's mount gate), so
  // the user can start typing without clicking into the box first.
  const focusSearchInput = useCallback((node) => {
    searchInputRef.current = node;
    if (node) {
      node.focus({ preventScroll: true });
      // The trigger button's click is still settling in the same tick on
      // some browsers and steals focus back; re-assert on the next frame.
      requestAnimationFrame(() => {
        if (searchInputRef.current === node && document.activeElement !== node) {
          node.focus({ preventScroll: true });
        }
      });
    }
  }, []);

  // When the caller owns the search, the options ARE the result — filtering
  // them again locally would hide rows the server matched on something the
  // label does not show (an admission number, an employee id).
  const filteredOptions = onSearchChange ? options : options?.filter(option => {
    if (!searchTerm) return true;
    const label = option?.label || option;
    // `keywords` lets an option match search text that isn't in its
    // visible label — e.g. a subject option matching its subject CODE
    // while the label stays the plain name. The label slot keeps the
    // old String() coercion so falsy-but-real labels (0, '') still match.
    const haystack = [String(label ?? ''), option?.keywords]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  // Stable, convention-safe testid fragment from an option value —
  // lowercased, hyphen-separated, never containing spaces; 'none' when
  // the value is empty so ids never end in a dangling dash.
  const toTestIdPart = (v) =>
    String(v ?? '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'none';

  return (
    <div className={twMerge('relative', optionalClasses)} style={sizingStyles} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        style={customStyles}
        className={twMerge(
          styleClasses,
          'flex items-center justify-between w-full',
          className
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid={dataTestId}
        {...props}
      >
        <span className="truncate">{selectedValue}</span>
        <svg
          className={twMerge(
            'w-4 h-4 ml-2 transition-transform duration-200',
            isOpen ? 'rotate-180' : ''
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <Portal>
          <div
            ref={dropdownMenuRef}
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`
            }}
            className={twMerge(
              `${zIndex} max-h-60 overflow-y-auto bg-dropdown-background border border-dropdown-border rounded-md shadow-lg`
            )}
            data-dropdown-portal="true"
          >
            {searchable && (
              <div className="sticky top-0 p-2 bg-dropdown-background border-b border-dropdown-border z-10">
                <input
                  ref={focusSearchInput}
                  type="text"
                  data-testid={dataTestId ? `${dataTestId}-search` : undefined}
                  className="w-full px-2 py-1 text-sm border border-dropdown-border rounded bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-background"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  // Auto-focus means Enter now lands here far more often;
                  // never let it submit a surrounding form.
                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                />
                {menuNote ? (
                  <p className="mt-1.5 px-0.5 text-[11px] leading-snug text-text-secondary">
                    {menuNote}
                  </p>
                ) : null}
              </div>
            )}
            {filteredOptions?.length > 0 ? (
              filteredOptions?.map((option, index) => {
                const isChecked = multiSelect && (Array.isArray(value) ? value : []).includes(optValueOf(option));
                return (
                <button
                  key={index}
                  type="button"
                  data-testid={dataTestId ? `${dataTestId}-option-${toTestIdPart(option?.value ?? option)}` : undefined}
                  onClick={() => handleSelect(option)}
                  className={twMerge(
                    'block w-full px-3 py-2 text-left hover:bg-background-hover focus:bg-background-hover focus:outline-none transition-colors duration-150',
                    isChecked ? 'bg-primary-background/5' : '',
                    option?.rowClassName,
                  )}
                >
                  {multiSelect ? (
                    <span className="flex items-center gap-2">
                      <span
                        className={twMerge(
                          'w-4 h-4 shrink-0 rounded border flex items-center justify-center text-[10px] leading-none',
                          isChecked ? 'bg-primary-background border-primary-background text-white' : 'border-dropdown-border text-transparent',
                        )}
                      >
                        ✓
                      </span>
                      <span className="min-w-0">{renderOption ? renderOption(option) : (option?.label || option)}</span>
                    </span>
                  ) : (
                    renderOption ? renderOption(option) : (option?.label || option)
                  )}
                </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-text-muted text-sm">No options found</div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}

export default Dropdown
